/**
 * Join refusals that fail the same way on every retry.
 *
 * A room's onAuth can refuse a join outright. Colyseus delivers the thrown
 * message as the joinOrCreate rejection (ServerError.message). Retrying such a
 * join only creates and disposes another empty room: in October 2026 a guest on
 * helix2000.com sat on "Connecting to the world…" while the client rejoined a
 * map the server refused guests on, about once a second (lane AJ, D27).
 *
 * The client stops on these and shows the refusal with a way forward.
 * Transient failures (network errors, a draining server) keep retrying.
 */

import { WORLD_TRANSFER_RECEIPT_REFUSED } from '../../shared/types/WorldTransfer';

export interface JoinRefusal {
  /** The server's wire code (the onAuth error message). */
  code: string;
  title: string;
  message: string;
  /** Signing in would change the answer (the refusal is about being a guest). */
  offerSignIn: boolean;
  /** Joining again from this window would take the hero back (it is in use elsewhere). */
  offerPlayHere?: boolean;
}

type RefusalCopy = Omit<JoinRefusal, 'code'>;

/** True when onAuth refused the join's transfer receipt (plan 26 F1). */
export function isTransferReceiptRefusal(error: unknown): boolean {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() === WORLD_TRANSFER_RECEIPT_REFUSED;
}

/**
 * The server closed this window's room with 4000 because the same hero joined
 * from another window or tab (a newer session owns the character). Rejoining
 * by itself would kick that window, which rejoins and kicks this one: two tabs
 * on one hero traded the seat about once a second on helix2000.com
 * (2026-10-10). Only the player's own "Play here" takes the hero back.
 */
export const SESSION_TAKEN_OVER = 'sessionTakenOver';

const TERMINAL_JOIN_REFUSALS: Readonly<Record<string, RefusalCopy>> = {
  [SESSION_TAKEN_OVER]: {
    title: 'Playing in another window',
    message: 'This hero entered the world from another tab or window, so this one stepped aside. Play here to bring your hero back to this window.',
    offerSignIn: false,
    offerPlayHere: true,
  },
  // src/server/auth/guestMapAccess.ts — a guest outside the guest maps. The copy
  // promises only the story: on helix2000.com the production gate narrows guests
  // to the story and dj-live-lobby (D40/D44), so "party modes" was untrue there.
  guestModeRestricted: {
    title: 'Guests can’t go here',
    message: 'As a guest you can play the story. Sign in with Twitch to go everywhere, or head back to the menu.',
    offerSignIn: true,
  },
  // src/server/auth/productionMapAccess.ts — production, a non-GM outside the
  // story, party and public mode maps (D40). The menu resumes the hero on an
  // open map (the roster sends a hero saved on a closed map to the story start).
  mapClosedInProduction: {
    title: 'That place isn’t open',
    message: 'This part of the world isn’t open to players. Head back to the menu to continue your story.',
    offerSignIn: false,
  },
  // world-transfer/authorizeReceiptJoin.ts — the join's receipt was refused.
  // connection.ts first drops the receipt and joins once without it; this
  // copy shows only if a refusal still arrives with no receipt left to drop.
  [WORLD_TRANSFER_RECEIPT_REFUSED]: {
    title: 'That crossing has closed',
    message: 'The way you came through is no longer open. Head back to the menu to continue your story.',
    offerSignIn: false,
  },
  // GameRoomLifecycle.onAuth — a transfer receipt without a destination map.
  worldTransferDestinationMissing: {
    title: 'That way is closed',
    message: 'This crossing has no destination. Head back to the menu and try again.',
    offerSignIn: false,
  },
};

const REASON_PREFIX = 'Join refused: ';

function refusalFor(code: string): JoinRefusal | null {
  if (!Object.prototype.hasOwnProperty.call(TERMINAL_JOIN_REFUSALS, code)) return null;
  return { code, ...TERMINAL_JOIN_REFUSALS[code] };
}

/** Classify a joinOrCreate rejection. Null means "retry may help". */
export function classifyJoinRefusal(error: unknown): JoinRefusal | null {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? refusalFor(message.trim()) : null;
}

/** The disconnect reason the network layer relays for a refused join. */
export function joinRefusalReason(refusal: Pick<JoinRefusal, 'code'>): string {
  return `${REASON_PREFIX}${refusal.code}`;
}

/** Read a refusal back out of a relayed disconnect reason. */
export function joinRefusalFromReason(reason: string | null | undefined): JoinRefusal | null {
  if (typeof reason !== 'string' || !reason.startsWith(REASON_PREFIX)) return null;
  return refusalFor(reason.slice(REASON_PREFIX.length));
}
